import { useCallback, useState, useEffect } from 'react'
import { format, parseISO } from 'date-fns'

import { Fetch } from '../../../../function/Fetch'
import type { PickupParticularFair } from '../../BridalFairs.types'

type OptionProps = {
  page?: number
  per?: number
  isPickup?: boolean
  filter?: {
    tags?: string[]
    startDate?: Date
    endDate?: Date
  }
}

export type ImageDataProps = {
  url: string
  oneToOneImage?: { url: string }
  threeToTwoImage?: { url: string }
  fourToThreeImage?: { url: string }
  sixteenToNineImage?: { url: string }
}

type RawBridalFairParticularDetail = {
  id: string
  isIndividuallyEdited: boolean
  isPrimary: boolean
  isNetReservable: boolean
  particularDate: string
  name: string
  descriptionText: string
  imageUrl: string
  imageData: ImageDataProps | null
  pricing: 'free' | 'paid' | 'partially_paid'
  numberOfPeople: number
  price: number
  pricingDescriptionText: string
  bridalFairDetail: {
    key: string
    order: number
    name: string
    descriptionText: string
    // imageUrl: string
    pricing: 'free' | 'paid' | 'partially_paid'
    numberOfPeople: number
    price: number
    pricingDescriptionText: string
    netResvDeadlineDays: number
    netResvDeadlineTime: string
    fairContents: {
      name: string
      descriptionText: string
      order: number
      // imageUrl: string
    }[]
    fairTags: {
      name: string
    }[]
    bridalSpecialOffers: {
      name: string
      descriptionText: string
      order: number
      // imageUrl: string
      applicableType: 'for_visitors' | 'for_success_customer'
    }[]
  }
  bridalFair: {
    minutes: number
  }
  fairTimes: {
    startAt: string
    availability: 'none' | 'few' | 'lot'
  }[]
}

export const usePickupBridalFairParticularDetails = (
  pageKey: string,
  { page = 1, per = 10, isPickup = true, filter = {} }: OptionProps,
) => {
  const [pickupParticularFairs, setPickupRecommendedParticularFairs] = useState<PickupParticularFair[]>([])

  const refresh = useCallback(() => {
    ;(async () => {
      const url = `/customer/api/v1/bridal_fair_pages/${pageKey}/bridal_fair_particular_details?start_date=${
        filter.startDate ? format(filter.startDate, 'yyyyMMdd') : ''
      }&end_date=${filter.endDate ? format(filter.endDate, 'yyyyMMdd') : ''}&tags=${
        filter.tags?.join() || ''
      }&page=${page}&per=${per}&is_pickup=${isPickup}`
      const res = await Fetch(url, 'GET', {})
      const json = (await res.json()) as { bridalFairParticularDetails: RawBridalFairParticularDetail[] }
      const transformedParticularFairs: PickupParticularFair[] = json.bridalFairParticularDetails.map((detail) => ({
        name: detail.name,
        id: detail.id,
        isPrimary: detail.isPrimary,
        particularDate: detail.particularDate,
        imageUrl: detail.imageUrl,
        imageData: detail.imageData,
        fairTimes: detail.fairTimes.map((fairTime) => ({
          startAt: parseISO(fairTime.startAt), // 文字列からDateオブジェクトに変換
          availability: fairTime.availability,
        })),
        bridalFairDetail: {
          key: detail.bridalFairDetail.key,
          order: detail.bridalFairDetail.order,
          pricing: detail.bridalFairDetail.pricing,
          numberOfPeople: detail.bridalFairDetail.numberOfPeople,
          netResvDeadlineDays: detail.bridalFairDetail.netResvDeadlineDays,
          netResvDeadlineTime: detail.bridalFairDetail.netResvDeadlineTime,
        },
        bridalFair: {
          minutes: detail.bridalFair.minutes,
        },
      }))
      setPickupRecommendedParticularFairs(transformedParticularFairs)
    })()
  }, [pageKey, page, per, filter, isPickup])

  useEffect(() => {
    refresh()
  }, []) // eslint-disable-line react-app/react-hooks/exhaustive-deps

  return { pickupParticularFairs }
}
